/*
 * Fabric samples media
 *
 * Everything beyond the plain swatch image on the fabric samples page:
 *
 * - Card: a small sofa thumbnail (with a 360° badge) in the swatch corner. On
 *   desktop, hovering it swaps the card image to the sofa. Clicking it opens
 *   the fabric details modal on its 360° view.
 * - Details modal: a row of views under the main image - the swatch, a 360°
 *   view of the fabric on a sofa, and any lifestyle photos uploaded for the
 *   fabric. Picking another colour in the modal rebuilds the row for that
 *   fabric and keeps the same kind of view open.
 *
 * The sofa renders come from Cylindo. Still frames are plain image URLs:
 *   https://content.cylindo.com/api/v2/{customer}/products/{code}/frames/{frame}/{code}.webp
 *     ?size=600&feature=UPHOLSTERY:AMICI-FERN&feature=ORIENTATION:RIGHT
 * and the 360° view is Cylindo's viewer (the one the product pages use), whose
 * script is only requested the first time a 360° view is opened. Fabrics
 * Cylindo has not modelled return a 404, and their sofa thumbnails are dropped.
 *
 * Lifestyle photos come from each product's `lifestyleImages`, which
 * snippets/fabric-samples.liquid fills from a list-of-files metafield.
 *
 * Loaded before fabric-samples-renderer.js, which calls cardThumbHtml(),
 * modalStageHtml() and modalThumbsHtml() while building its markup;
 * fabric-samples.js calls the onModal*() hooks.
 */
window.FabricSamplesMedia = (() => {
  const CARD_IMAGE_SIZE = 600;
  const THUMB_SIZE = 120;
  const SWATCH_HINT = "tap &amp; pan to zoom";

  const host = document.querySelector("collection-fabric-samples");

  // Cylindo settings from the section. Null when the sofa preview is off.
  let cylindo = null;
  try {
    cylindo = host?.dataset.sofaPreview ? JSON.parse(host.dataset.sofaPreview) : null;
  } catch (e) {
    cylindo = null;
  }
  if (cylindo && !cylindo.code) cylindo = null;

  const extraFeatures = String(cylindo?.features || "")
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);

  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)");

  function escapeAttr(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  /* ---------- Cylindo ---------- */

  // Same normalisation the sofa builder uses to match our fabric titles to
  // Cylindo's UPHOLSTERY codes: "Amici Fern" -> "AMICI-FERN".
  function toCylindoValue(title) {
    return String(title || "")
      .toUpperCase()
      .trim()
      .replace(/[\s_-]+/g, "-");
  }

  function frameUrl(fabricValue, frame, size) {
    const code = encodeURIComponent(cylindo.code);
    const params = [`size=${size}`, `feature=UPHOLSTERY:${encodeURIComponent(fabricValue)}`]
      .concat(extraFeatures.map((f) => `feature=${encodeURIComponent(f).replace("%3A", ":")}`))
      .join("&");
    return `https://content.cylindo.com/api/v2/${encodeURIComponent(cylindo.customerId)}/products/${code}/frames/${frame}/${code}.webp?${params}`;
  }

  // Whether Cylindo has a model for a fabric: true, false, or a pending
  // Promise<boolean>. Filled in by every sofa thumbnail that loads or fails.
  const modelAvailability = new Map();

  function hasModel(fabricValue) {
    const known = modelAvailability.get(fabricValue);
    if (known !== undefined) return Promise.resolve(known);

    const pending = new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve(true);
      probe.onerror = () => resolve(false);
      probe.src = frameUrl(fabricValue, cylindo.frame, THUMB_SIZE);
    }).then((available) => {
      modelAvailability.set(fabricValue, available);
      return available;
    });
    modelAvailability.set(fabricValue, pending);
    return pending;
  }

  // load/error do not bubble, so listen in the capture phase.
  function onSofaThumbSettled(event) {
    const img = event.target;
    if (!(img instanceof HTMLImageElement) || !img.matches("[data-sofa-frame]")) return;

    const available = event.type === "load";
    modelAvailability.set(img.dataset.sofaFrame, available);

    const cardPreview = img.closest("[data-sofa-card-preview]");
    if (cardPreview) {
      if (available) cardPreview.classList.add("is-loaded");
      else cardPreview.remove();
      return;
    }

    const modalThumb = img.closest('[data-media-view="3d"]');
    if (modalThumb && !available) dropSofaView(modalThumb);
  }
  document.addEventListener("load", onSofaThumbSettled, true);
  document.addEventListener("error", onSofaThumbSettled, true);

  // The viewer's `configuration` attribute: "UPHOLSTERY:AMICI-FERN,ORIENTATION:RIGHT".
  // Each key and value is URI-decoded by the viewer, so encode them here.
  function viewerConfiguration(fabricValue) {
    return [`UPHOLSTERY:${fabricValue}`]
      .concat(extraFeatures)
      .map((pair) => {
        const [key, ...rest] = pair.split(":");
        return `${encodeURIComponent(key.trim())}:${encodeURIComponent(rest.join(":").trim())}`;
      })
      .join(",");
  }

  const VIEWER_SCRIPTS = ["https://viewer-cdn.cylindo.com/v1/index.mjs"];
  let viewerReady = null;

  function loadViewer() {
    if (!viewerReady) {
      viewerReady = Promise.all(VIEWER_SCRIPTS.map((src) => import(src)))
        .then(() => customElements.whenDefined("cylindo-viewer"))
        .catch((error) => {
          viewerReady = null;
          throw error;
        });
    }
    return viewerReady;
  }

  function createViewer(fabricValue) {
    const viewer = document.createElement("cylindo-viewer");
    viewer.setAttribute("customer-id", cylindo.customerId);
    viewer.setAttribute("code", cylindo.code);
    viewer.setAttribute("ar", "false");
    viewer.setAttribute("controls", "zoom indicators");
    viewer.setAttribute("show-zoom-controls-on-touch", "true");
    // Set up front so the first frames load in this fabric, not the default.
    viewer.setAttribute("configuration", viewerConfiguration(fabricValue));
    viewer.className = "fabric-samples-media-viewer";

    const spin = document.createElement("cylindo-360");
    spin.setAttribute("interaction-prompt", "auto");
    spin.setAttribute("remove-environment-shadow", "false");
    // Only the 360 spin: no still frames or thumbnail bar.
    viewer.appendChild(spin);
    return viewer;
  }

  /* ---------- Card: sofa thumbnail ---------- */

  function cardThumbHtml(product) {
    if (!cylindo || !product || !product.title) return "";
    const value = toCylindoValue(product.title);
    if (modelAvailability.get(value) === false) return "";
    const label = `See ${product.title} on the ${cylindo.name} in 360°`;

    // The hint sits outside the button, which clips its contents. The preview
    // stays invisible until its render loads (see onSofaThumbSettled).
    return `<span class="fabric-sofa-preview" data-sofa-card-preview>
      <button type="button" class="fabric-sofa-thumb" data-sofa-thumb
          data-sofa-fabric="${escapeAttr(value)}"
          data-sofa-title="${escapeAttr(product.title)}"
          aria-label="${escapeAttr(label)}">
        <img class="fabric-sofa-thumb__sofa" data-sofa-frame="${escapeAttr(value)}"
             src="${frameUrl(value, cylindo.frame, THUMB_SIZE)}"
             alt="" width="56" height="56" loading="lazy" decoding="async" fetchpriority="low">
        <img class="fabric-sofa-thumb__swatch" src="${escapeAttr(product.imageNav)}"
             alt="" width="56" height="56" loading="lazy" decoding="async" fetchpriority="low">
        <span class="fabric-sofa-thumb__badge" aria-hidden="true">360°</span>
      </button>
      <span class="fabric-sofa-hint" aria-hidden="true">Click to view in 360°</span>
    </span>`;
  }

  function showSofaOnCard(thumb) {
    const wrapper = thumb.closest(".fabric-samples-wrapper");
    if (!wrapper) return;

    let sofaImage = wrapper.querySelector(".fabric-samples-sofa-image");
    if (!sofaImage) {
      sofaImage = document.createElement("img");
      sofaImage.className = "fabric-samples-sofa-image";
      sofaImage.alt = `${thumb.dataset.sofaTitle} on the ${cylindo.name}`;
      sofaImage.width = CARD_IMAGE_SIZE;
      sofaImage.height = CARD_IMAGE_SIZE;
      sofaImage.src = frameUrl(thumb.dataset.sofaFabric, cylindo.frame, CARD_IMAGE_SIZE);
      wrapper.querySelector(".fabric-samples-image").after(sofaImage);
    }
    wrapper.classList.add("is-showing-sofa");
  }

  function hideSofaOnCard(thumb) {
    thumb.closest(".fabric-samples-wrapper")?.classList.remove("is-showing-sofa");
  }

  // Opens the fabric details modal for the card, on its 360° view.
  function openModalFromCard(thumb) {
    const renderer = window.FabricSamplesRenderer;
    const card = thumb.closest(".fabric-samples-card");
    const product = renderer?.getProductByVariantId(parseInt(card?.dataset.productId, 10));
    if (!product || typeof host?.openProductModal !== "function") return;

    // A "Most Popular" card belongs to that block too; the modal lists the
    // other colours of the fabric's own collection, as a card click does.
    let blockId = thumb.closest("[data-block-id]")?.dataset.blockId;
    for (const [id, { block, products }] of renderer.getBlockProductMap()) {
      if (block.title === "Most Popular") continue;
      if (products.some((p) => p.id === product.id)) {
        blockId = id;
        break;
      }
    }
    if (!blockId) return;

    requestedView = "3d";
    host.openProductModal(product.id, blockId, thumb);
    requestedView = null;
  }

  document.addEventListener("mouseover", (event) => {
    if (!canHover.matches) return;
    const thumb = event.target.closest?.("[data-sofa-thumb]");
    if (!thumb || thumb.contains(event.relatedTarget)) return;
    showSofaOnCard(thumb);
  });

  document.addEventListener("mouseout", (event) => {
    const thumb = event.target.closest?.("[data-sofa-thumb]");
    if (!thumb || thumb.contains(event.relatedTarget)) return;
    hideSofaOnCard(thumb);
  });

  // Capture phase: the card itself toggles the cart on click and on
  // Enter/Space, and its image wrapper opens the modal on the swatch view.
  // The thumbnail must do neither.
  document.addEventListener(
    "click",
    (event) => {
      const thumb = event.target.closest?.("[data-sofa-thumb]");
      if (!thumb) return;
      event.preventDefault();
      event.stopPropagation();
      hideSofaOnCard(thumb);
      openModalFromCard(thumb);
    },
    true,
  );

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!event.target.closest?.("[data-sofa-thumb]")) return;
      // Let the button's native click fire, but keep the card from seeing the key.
      event.stopPropagation();
    },
    true,
  );

  /* ---------- Details modal: swatch / 360° / lifestyle views ---------- */

  // View to open the next modal on (set by a card's sofa thumbnail).
  let requestedView = null;
  // { contentEl, product, fabricValue, view, index } while the modal is open.
  let modal = null;
  // Guards against a slow 360° load landing after the view has moved on.
  let viewToken = 0;

  function lifestyleImages(product) {
    return Array.isArray(product?.lifestyleImages) ? product.lifestyleImages : [];
  }

  function hasSofaView(product) {
    return Boolean(cylindo) && modelAvailability.get(toCylindoValue(product.title)) !== false;
  }

  // Empty host the 360° viewer or a lifestyle photo is placed in; it takes
  // the swatch image's place while one of those views is open.
  function modalStageHtml() {
    return '<div class="fabric-samples-media-stage" data-media-stage hidden></div>';
  }

  function thumbsInnerHtml(product) {
    const photos = lifestyleImages(product);
    const showSofa = hasSofaView(product);
    // With nothing to switch to, the row would be a lone swatch thumbnail.
    if (!showSofa && photos.length === 0) return "";

    const fabricValue = toCylindoValue(product.title);
    let html = `<button type="button" class="fabric-samples-media-thumb" data-media-view="swatch"
        aria-label="Fabric swatch" aria-pressed="false">
      <img src="${escapeAttr(product.imageNav)}" alt="" width="64" height="64">
    </button>`;

    if (showSofa) {
      html += `<button type="button" class="fabric-samples-media-thumb" data-media-view="3d"
          aria-label="360° view on the ${escapeAttr(cylindo.name)}" aria-pressed="false">
        <img data-sofa-frame="${escapeAttr(fabricValue)}" src="${frameUrl(fabricValue, cylindo.frame, THUMB_SIZE)}"
             alt="" width="64" height="64">
        <span class="fabric-samples-media-thumb__badge" aria-hidden="true">360°</span>
      </button>`;
    }

    photos.forEach((photo, index) => {
      html += `<button type="button" class="fabric-samples-media-thumb" data-media-view="lifestyle"
          data-media-index="${index}" aria-label="Lifestyle photo ${index + 1}" aria-pressed="false">
        <img src="${escapeAttr(photo.thumb)}" alt="" width="64" height="64" loading="lazy">
      </button>`;
    });

    return html;
  }

  function modalThumbsHtml(product) {
    return `<div class="fabric-samples-media-thumbs" data-media-thumbs role="group" aria-label="Views">${thumbsInnerHtml(product)}</div>`;
  }

  function modalParts() {
    const contentEl = modal.contentEl;
    return {
      stage: contentEl.querySelector("[data-media-stage]"),
      thumbs: contentEl.querySelector("[data-media-thumbs]"),
      swatch: contentEl.querySelector(".fabric-samples-image-container"),
      hint: contentEl.querySelector(".pinch-zoom-container"),
    };
  }

  function markActiveThumb(view, index) {
    modalParts().thumbs?.querySelectorAll("[data-media-view]").forEach((thumb) => {
      const active =
        thumb.dataset.mediaView === view &&
        (view !== "lifestyle" || parseInt(thumb.dataset.mediaIndex, 10) === index);
      thumb.classList.toggle("is-active", active);
      thumb.setAttribute("aria-pressed", String(active));
    });
  }

  function showSwatch() {
    const { stage, swatch, hint } = modalParts();
    viewToken++;
    modal.view = "swatch";
    modal.index = 0;
    if (stage) {
      stage.hidden = true;
      stage.className = "fabric-samples-media-stage";
      stage.replaceChildren();
    }
    if (swatch) swatch.hidden = false;
    if (hint) hint.innerHTML = SWATCH_HINT;
    markActiveThumb("swatch", 0);
  }

  function openStage(view, index, hintText) {
    const { stage, swatch, hint } = modalParts();
    modal.view = view;
    modal.index = index;
    if (swatch) swatch.hidden = true;
    stage.hidden = false;
    stage.className = `fabric-samples-media-stage fabric-samples-media-stage--${view}`;
    // Keeps the line's height on mobile so the modal does not jump.
    if (hint) hint.innerHTML = hintText || "&nbsp;";
    markActiveThumb(view, index);
    return stage;
  }

  function showLifestyle(index) {
    const photo = lifestyleImages(modal.product)[index];
    if (!photo) return showSwatch();

    viewToken++;
    const stage = openStage("lifestyle", index);
    const img = document.createElement("img");
    img.className = "fabric-samples-media-photo";
    img.src = photo.large;
    img.alt = photo.alt || `${modal.product.title} lifestyle photo`;
    stage.replaceChildren(img);
  }

  // The fabric turned out to have no Cylindo model: drop its 360° thumbnail
  // and, if that view is open, go back to the swatch.
  function dropSofaView(thumb) {
    thumb?.remove();
    if (!modal) return;
    const { thumbs } = modalParts();
    if (thumbs && thumbs.querySelectorAll("[data-media-view]").length < 2) thumbs.replaceChildren();
    if (modal.view === "3d") showSwatch();
  }

  function dropCurrentSofaView() {
    if (!modal) return;
    dropSofaView(modalParts().thumbs?.querySelector('[data-media-view="3d"]'));
  }

  function showSofa() {
    const fabricValue = modal.fabricValue;
    const token = ++viewToken;
    const stage = openStage("3d", 0, "drag to rotate");
    const isCurrent = () => token === viewToken && Boolean(modal);

    // Switching colour while the viewer is open: point it at the new fabric
    // rather than rebuilding it, so it keeps its angle.
    const current = stage.querySelector("cylindo-viewer");
    if (current) {
      hasModel(fabricValue).then((available) => {
        if (!isCurrent()) return;
        if (!available) return dropCurrentSofaView();
        current.setAttribute("configuration", viewerConfiguration(fabricValue));
      });
      return;
    }

    stage.classList.add("is-loading");
    stage.innerHTML = `<span class="fabric-samples-media-caption">On the ${escapeAttr(cylindo.name)}</span>`;

    Promise.all([hasModel(fabricValue), loadViewer()])
      .then(([available]) => {
        if (!isCurrent()) return;
        if (!available) return dropCurrentSofaView();

        const viewer = createViewer(fabricValue);
        viewer.addEventListener("loaded", () => stage.classList.remove("is-loading"), { once: true });
        stage.prepend(viewer);
      })
      .catch(() => {
        // The viewer script did not load; the swatch is still worth showing.
        if (isCurrent()) showSwatch();
      });
  }

  function setView(view, index) {
    if (!modal) return;
    if (view === "3d" && hasSofaView(modal.product)) return showSofa();
    if (view === "lifestyle") return showLifestyle(index || 0);
    showSwatch();
  }

  // fabric-samples.js: the modal was (re)built for `product`.
  function onModalRender(contentEl, product) {
    modal = { contentEl, product, fabricValue: toCylindoValue(product.title), view: "swatch", index: 0 };
    setView(requestedView || "swatch", 0);
  }

  // fabric-samples.js: another colour was picked inside the open modal.
  function onModalSwitch(contentEl, product) {
    if (!modal) return onModalRender(contentEl, product);

    const { view } = modal;
    modal.contentEl = contentEl;
    modal.product = product;
    modal.fabricValue = toCylindoValue(product.title);

    const { thumbs } = modalParts();
    if (thumbs) thumbs.innerHTML = thumbsInnerHtml(product);

    // Stay on the same kind of view; a lifestyle photo index belongs to the
    // previous fabric, so start from that fabric's first photo.
    setView(view, 0);
  }

  // fabric-samples.js: the modal closed. Removing the viewer stops it
  // fetching the remaining frames.
  function onModalClose() {
    if (!modal) return;
    viewToken++;
    modalParts().stage?.replaceChildren();
    modal = null;
  }

  document.addEventListener("click", (event) => {
    const thumb = event.target.closest?.("[data-media-thumbs] [data-media-view]");
    if (!thumb || !modal) return;
    setView(thumb.dataset.mediaView, parseInt(thumb.dataset.mediaIndex || "0", 10));
  });

  return {
    cardThumbHtml,
    modalStageHtml,
    modalThumbsHtml,
    onModalRender,
    onModalSwitch,
    onModalClose,
  };
})();
